import { supabase } from './supabase';

const toNumber = (value: unknown): number => {
  if (typeof value === 'number') return value;
  return Number(value || 0);
};

export interface ApAgingRow {
  supplier_id: string;
  supplier_name: string;
  invoice_id: string;
  invoice_number: string | null;
  invoice_date: string;
  due_date: string | null;
  total_amount: number;
  amount_paid: number;
  outstanding: number;
  status: string;
  days_outstanding: number;
  bucket: '0-30' | '31-60' | '61-90' | '90+';
}

export interface ApAgingSummary {
  totalOutstanding: number;
  current: number;
  days31to60: number;
  days61to90: number;
  days90plus: number;
  supplierCount: number;
}

export interface SupplierStatementRow {
  type: 'invoice' | 'payment';
  date: string;
  reference: string;
  description: string;
  debit: number;
  credit: number;
  balance: number;
}

const ageBucket = (days: number): ApAgingRow['bucket'] => {
  if (days <= 30) return '0-30';
  if (days <= 60) return '31-60';
  if (days <= 90) return '61-90';
  return '90+';
};

export const fetchApAgingReport = async (
  companyId: number,
  outletId?: string | null,
  supplierId?: string | null,
): Promise<{ rows: ApAgingRow[]; summary: ApAgingSummary }> => {
  const today = new Date().toISOString().slice(0, 10);

  let query = supabase
    .from('supplier_invoices')
    .select(
      'id, supplier_id, invoice_number, invoice_date, due_date, total_amount, amount_paid, status, inventory_suppliers!inner(name)',
    )
    .eq('company_id', companyId)
    .in('status', ['posted', 'partially_paid'])
    .order('invoice_date', { ascending: false });

  if (outletId) query = query.eq('outlet_id', outletId);
  if (supplierId && supplierId !== 'all')
    query = query.eq('supplier_id', supplierId);

  const { data, error } = await query;
  if (error) throw new Error(error.message);

  const rows: ApAgingRow[] = (data ?? []).map((row) => {
    const r = row as Record<string, unknown>;
    const supplier = r.inventory_suppliers as Record<string, unknown>;
    const total = toNumber(r.total_amount);
    const paid = toNumber(r.amount_paid);
    const outstanding = Math.max(0, total - paid);
    const refDate = (r.due_date as string | null) ?? (r.invoice_date as string);
    const msPerDay = 1000 * 60 * 60 * 24;
    const days = Math.max(
      0,
      Math.floor(
        (new Date(today).getTime() - new Date(refDate).getTime()) / msPerDay,
      ),
    );

    return {
      supplier_id: String(r.supplier_id || ''),
      supplier_name: String(supplier?.name || 'Unknown supplier'),
      invoice_id: String(r.id || ''),
      invoice_number: (r.invoice_number as string | null) ?? null,
      invoice_date: String(r.invoice_date || ''),
      due_date: (r.due_date as string | null) ?? null,
      total_amount: total,
      amount_paid: paid,
      outstanding,
      status: String(r.status || ''),
      days_outstanding: days,
      bucket: ageBucket(days),
    };
  });

  const summary: ApAgingSummary = {
    totalOutstanding: rows.reduce((s, r) => s + r.outstanding, 0),
    current: rows
      .filter((r) => r.bucket === '0-30')
      .reduce((s, r) => s + r.outstanding, 0),
    days31to60: rows
      .filter((r) => r.bucket === '31-60')
      .reduce((s, r) => s + r.outstanding, 0),
    days61to90: rows
      .filter((r) => r.bucket === '61-90')
      .reduce((s, r) => s + r.outstanding, 0),
    days90plus: rows
      .filter((r) => r.bucket === '90+')
      .reduce((s, r) => s + r.outstanding, 0),
    supplierCount: new Set(rows.map((r) => r.supplier_id)).size,
  };

  return { rows, summary };
};

export const fetchSupplierStatement = async (
  companyId: number,
  outletId: string | null,
  supplierId: string,
): Promise<SupplierStatementRow[]> => {
  const [invoicesResult, paymentsResult] = await Promise.all([
    supabase
      .from('supplier_invoices')
      .select('id, invoice_number, invoice_date, total_amount, status, notes')
      .eq('company_id', companyId)
      .eq('supplier_id', supplierId)
      .not('status', 'eq', 'draft')
      .order('invoice_date', { ascending: true }),
    supabase
      .from('supplier_payments')
      .select('id, payment_date, amount, reference, notes')
      .eq('company_id', companyId)
      .eq('supplier_id', supplierId)
      .order('payment_date', { ascending: true }),
  ]);

  if (invoicesResult.error) throw new Error(invoicesResult.error.message);
  if (paymentsResult.error) throw new Error(paymentsResult.error.message);

  type RawRow = {
    type: 'invoice' | 'payment';
    date: string;
    reference: string;
    description: string;
    debit: number;
    credit: number;
  };

  const combined: RawRow[] = [
    ...(invoicesResult.data ?? []).map((r) => ({
      type: 'invoice' as const,
      date: String(r.invoice_date || ''),
      reference:
        (r.invoice_number as string | null) ?? String(r.id || '').slice(0, 8),
      description: (r.notes as string | null) ?? 'Supplier invoice',
      debit: toNumber(r.total_amount),
      credit: 0,
    })),
    ...(paymentsResult.data ?? []).map((r) => ({
      type: 'payment' as const,
      date: String(r.payment_date || ''),
      reference: (r.reference as string | null) ?? '',
      description: (r.notes as string | null) ?? 'Payment',
      debit: 0,
      credit: toNumber(r.amount),
    })),
  ].sort((a, b) => a.date.localeCompare(b.date));

  let balance = 0;
  return combined.map((row) => {
    balance += row.debit - row.credit;
    return { ...row, balance };
  });
};
